import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Form, formResolver, RHFSelect } from '@components/Form';
import { ME_INCLUDE, useMe } from '@hooks/users';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import handleRequestError from '@utils/handleRequestError';
import { getWeekStart, isWeekStartDay, setWeekStart, type WeekStartDay } from '@utils/userConfig';

// RHFSelect value is a string (MenuItem values), narrowed back to WeekStartDay on submit.
type CalendarPreferencesFormValues = {
  weekStart: string;
};

const WEEK_DAYS: WeekStartDay[] = [0, 1, 2, 3, 4, 5, 6];

// A Sunday in UTC; adding `day` days yields each 0..6 weekday, formatted per locale.
const SUNDAY_REFERENCE_MS = Date.UTC(2023, 0, 1);
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const validationSchema = Yup.object({
  weekStart: Yup.string().required(),
});

type CalendarPreferencesDialogProps = {
  onClose: () => void;
};

export function CalendarPreferencesDialog({
  onClose,
}: CalendarPreferencesDialogProps): JSX.Element {
  const intl = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Shares QuickUserToggler's `me` cache (same ME_INCLUDE); the in-place patch on
  // success updates open calendars without a refetch, so no `mutationKey` needed.
  const { normalizedData, updateMyConfig, isPending } = useMe({
    include: ME_INCLUDE,
    onUpdateConfig: onClose,
    onError: handleRequestError,
  });

  // `/users/me` normalizes to a single resource; guard the array branch.
  const me = normalizedData && !Array.isArray(normalizedData) ? normalizedData : null;
  const config = typeof me?.config === 'string' ? me.config : undefined;
  const userId = typeof me?.id === 'string' ? me.id : undefined;

  const weekDayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(intl.locale, {
      weekday: 'long',
      timeZone: 'UTC',
    });
    return WEEK_DAYS.map((day) =>
      formatter.format(new Date(SUNDAY_REFERENCE_MS + day * ONE_DAY_MS))
    );
  }, [intl.locale]);

  // `values` reactively re-syncs the select once the `me` read resolves — no effect,
  // and no one-frame render of the default before it lands.
  const methods = useForm<CalendarPreferencesFormValues>({
    values: { weekStart: String(getWeekStart(config)) },
    resolver: formResolver<CalendarPreferencesFormValues>(validationSchema),
  });

  const onSubmit = async ({ weekStart }: CalendarPreferencesFormValues): Promise<void> => {
    if (!userId) return;
    const day = Number(weekStart);
    if (!isWeekStartDay(day)) return;
    // Read-modify-write against the latest blob at execution time (see useMe) so a concurrent
    // row-order drag can't drop this preference and vice versa.
    await updateMyConfig({ userId, updateConfig: (current) => setWeekStart(current, day) });
  };

  return (
    <Dialog fullWidth open fullScreen={fullScreen} maxWidth="xs" onClose={onClose}>
      <DialogTitle>Calendar settings</DialogTitle>

      <Form methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <RHFSelect label="First day of the week" name="weekStart" sx={{ mt: 1 }}>
            {WEEK_DAYS.map((day) => (
              <MenuItem key={day} value={String(day)}>
                {weekDayLabels[day]}
              </MenuItem>
            ))}
          </RHFSelect>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isPending || !userId} type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
